/**
 * Page CRDT Service
 *
 * Thin wrapper around localFirstSyncService for Page entities.
 *
 * Provides 4-layer sync:
 * - IndexedDB (crash recovery)
 * - BroadcastChannel (cross-tab sync)
 * - WebSocket via page: room (real-time + server persistence)
 * - HTTP debounced (entity.skeleton.base64 backend save)
 *
 * Each page has a Y.Doc with:
 * - Y.XmlFragment('prosemirror') — ProseMirror document content
 */

import { localFirstSyncService } from '../../../../services/sync/localFirstSyncService';
import useEntityStore from '../../../../stores/entityStore';

// Use a fixed workspace key so the IndexedDB slot is stable across sessions.
// Page entity IDs are globally unique MongoDB ObjectIds so there's no cross-workspace
// collision risk, and a dynamic key (from currentDataspaceKey) causes blank pages when
// the store's workspace changes between open and close.
const PAGE_WORKSPACE_KEY = 'default';

class PageCRDTService {
  /**
   * Get Y.Doc for a page with all sync providers ready.
   * Waits for IndexedDB to load before returning so the Y.Doc
   * has local content before any writes are attempted.
   */
  async getYDocForPage(pageId: string) {
    return localFirstSyncService.waitForReady(pageId, 'Page', PAGE_WORKSPACE_KEY);
  }

  /**
   * Clean up all providers for a page when the component unmounts.
   */
  cleanup(pageId: string) {
    localFirstSyncService.cleanup(pageId, 'Page', PAGE_WORKSPACE_KEY);
  }
}

export const pageCRDTService = new PageCRDTService();
